import { AsyncCtx, atom, reatomAsync } from "@reatom/framework";
import { MessageItem, MessagesInterface } from "./types";
import { getMessages, sendMessage } from "@/core/api/requests/messages";
import { uploadSessionAtom, uploadFileToSessionAtom } from "../upload/upload";

interface PendingMessage {
  id: number;
  text?: string;
  reply_to?: number;
  time: number;
  isPending: boolean;
}

export const messagesAtom = atom<MessagesInterface | null>(
  null,
  "messagesAtom"
);

export const replyedMessageAtom = atom<MessageItem | null>(
  null,
  "replyedMessageAtom"
);

export const pendingMessagesAtom = atom<PendingMessage[]>(
  [],
  "pendingMessagesAtom"
);

export const isLoadingMessagesAtom = atom(false, "isLoadingMessagesAtom");

export const getMessagesAction = reatomAsync(
  async (
    ctx: AsyncCtx,
    id: number,
    offset: number = 0,
    isLoading?: boolean,
    complete?: boolean
  ) => {
    if (!isLoading) {
      isLoadingMessagesAtom(ctx, true);
    }
    let response;
    const currentMessages = ctx.get(messagesAtom);
    if (complete) {
      response = await getMessages({
        order_id: id,
        limit: 10,
        offset: offset,
        since: currentMessages?.last_message_time,
      });
    } else {
      response = await getMessages({ order_id: id, limit: 10, offset: offset });
    }
    return { response, complete, offset };
  },
  {
    name: "getMessagesAction",
    onFulfill: (_ctx, { response, complete, offset }) => {
      isLoadingMessagesAtom(_ctx, false);
      const prevMessages = _ctx.get(messagesAtom);
      if (offset && prevMessages) {
        messagesAtom(_ctx, {
          ...prevMessages,
          items: [...prevMessages.items, ...response.items],
          has_next_page: response.has_next_page,
        });
        return;
      }

      if (complete && prevMessages) {
        if (response.items.length) {
          messagesAtom(_ctx, {
            ...prevMessages,
            last_message_time: response.last_message_time,
            items: [...response.items, ...prevMessages.items],
          });
        }
      } else {
        messagesAtom(_ctx, response);
      }
    },
    onReject: (_ctx) => {
      isLoadingMessagesAtom(_ctx, false);
    },
  }
);

export const sendMessagesAction = reatomAsync(
  async (
    ctx: AsyncCtx,
    message: {
      id: number;
      text?: string;
      reply_to?: number;
      file_session_id?: string;
    }
    // id: number,
    // text: string,
    // reply_to?: number,
    // file_session_id?: string
  ) => {
    const tempMessage = {
      id: Date.now(),
      text: message.text,
      reply_to: message.reply_to,
      time: Date.now(),
      isPending: true,
    };

    const prevPending = ctx.get(pendingMessagesAtom);
    pendingMessagesAtom(ctx, [...prevPending, tempMessage]);
    const response = await sendMessage({
      order_id: message.id,
      text: message.text,
      reply_to: message.reply_to,
      file_session_id: message.file_session_id,
    });
    return { response, tempMessage };
  },
  {
    name: "sendMessagesAction",
    onFulfill: (_ctx, data) => {
      const prev = _ctx.get(messagesAtom);
      if (prev) {
        const newAtom = {
          ...prev,
          last_message_time: data.response.time,
          items: [data.response, ...prev.items],
        };
        const prevPending = _ctx.get(pendingMessagesAtom);
        pendingMessagesAtom(
          _ctx,
          prevPending.filter((item) => item.id !== data.tempMessage.id)
        );
        messagesAtom(_ctx, newAtom);
      }
      uploadSessionAtom(_ctx, null);
      uploadFileToSessionAtom(_ctx, null);
    },
  }
);
